import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {MDBCol, MDBIcon, MDBRow} from "mdbreact";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {Checkbox, DatePicker, Drawer, Layout, Popover, Select, Table, Tabs, Timeline, Divider} from "antd";
import {Input} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getDoc, getDocs, getFirestore, query, serverTimestamp, updateDoc, where} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {getFunctions, httpsCallable} from "firebase/functions";
import {ColumnsType} from "antd/lib/table/interface";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {Tag} from "primereact/tag";
import {useToast} from "@chakra-ui/react";
import type {Dayjs} from "dayjs";
import {Link, useSearchParams} from "react-router-dom";


const db = getFirestore(Firebase);
const adminOverridePayoutToSeller = httpsCallable(getFunctions(Firebase), 'adminOverridePayoutToSeller');
const {Content} = Layout;
const {TabPane} = Tabs;
const {RangePicker} = DatePicker;
const {Option} = Select;

interface DataType {
    key: string;         // Firestore doc id
    orderId: string;
    orderNo: string;
    buyer: string;
    seller: string;
    product: string;
    amount: string;
    rawAmount: number;
    statusText: string;
    status: JSX.Element;
    paymentProvider: string;
    paymentMethod: string;
    paymentStatusText: string;   // buyer payment status (direct field for reliable column render)
    payoutStatus: string;
    buyerPayoutStatus: string;   // refund-to-buyer status. Same payoutStatus/payoutAttempts
                                  // fields as the seller column above are reused here — an
                                  // order is only ever a seller payout OR a buyer refund, so
                                  // this reads them as a refund only when the order is actually
                                  // cancelled/refund-accepted (see the mapping below).
    ledgerVersion?: number;
    paidOutAt?: any;
    clearsAt?: any;
    clearedAt?: any;
    dateMs: number;      // ms timestamp for sorting
    date: string;
    updatedAtMs: number;  // last-modified ms timestamp for sorting
    updatedAt: string;
    raw: any;
}

function numberWithCommas(x: string | number) {
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// A handful of legacy orders have buyerName/sellerName stored as a delivery-info
// object ({name, phone, city, address}) instead of a plain string — guard so
// those never get rendered directly as a React child.
function displayName(v: any): string | undefined {
    if (typeof v === 'string' && v.trim()) return v;
    if (v && typeof v === 'object' && typeof v.name === 'string' && v.name.trim()) return v.name;
    return undefined;
}

// Firestore Timestamps have a toDate() method; plain strings/Dates also handled
function parseFirestoreDate(val: any): moment.Moment | null {
    if (!val) return null;
    if (typeof val.toDate === 'function') return moment(val.toDate());
    if (val instanceof Date) return moment(val);
    if (typeof val === 'number') return moment(val);
    return moment(val, ['DD MMM YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
}

function statusSeverity(s: string): any {
    const l = (s ?? '').toLowerCase();
    if (['completed', 'delivered', 'confirmed', 'successful', 'success', 'active', 'paid', 'overridden'].includes(l)) return 'success';
    if (['pending', 'submitted', 'enqueued', 'awaiting'].includes(l)) return 'warning';
    if (['cancelled', 'canceled', 'failed', 'rejected', 'error', 'exhausted'].includes(l)) return 'danger';
    return 'info';
}

// Derives the seller-wallet ledger payout state directly from the order doc's own
// ledger fields (clearsAt/clearedAt/paidOutAt), rather than the legacy per-order
// payoutStatus/payoutAttempts fields the on-confirm Cloud Function used to write.
// That old CF (payout_seller_on_confirm.js) was deleted when the app moved to a
// ledger-based wallet (sellerBalances + append-only ledger) — orderStatus now stays
// 'Confirmed' forever and payoutStatus is never updated again, so any order on the
// ledger system (ledgerVersion === 1) must be read this way instead. Returns null
// for pre-ledger orders, so callers can fall back to the legacy fields for those.
type LedgerPayoutState = { label: string; severity: 'success' | 'warning' | 'info'; date?: any };

function ledgerPayoutState(order: any): LedgerPayoutState | null {
    if (order?.ledgerVersion !== 1) return null;
    if (order.paidOutAt) return {label: 'Paid', severity: 'success', date: order.paidOutAt};
    if (order.clearedAt) return {label: 'Available (awaiting payout)', severity: 'warning', date: order.clearedAt};
    if (order.clearsAt) return {label: 'Clearing', severity: 'info', date: order.clearsAt};
    return {label: 'Pending clearance', severity: 'info'};
}

// receiptImages/shipmentPhotos elements are either a legacy plain URL string
// or an OrderPhoto object {url, uploadedAt, removedAt, removedBy}.
function photoUrl(photo: any): string {
    return typeof photo === 'string' ? photo : photo?.url ?? '';
}
function photoRemovedAt(photo: any): any {
    return typeof photo === 'string' ? null : photo?.removedAt ?? null;
}

function EvidenceThumbnails({photos, alt}: {photos: any[]; alt: string}) {
    if (!photos?.length) return null;
    return (
        <div style={{display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap'}}>
            {photos.map((photo: any, i: number) => {
                const url = photoUrl(photo);
                const removedAt = photoRemovedAt(photo);
                return (
                    <a key={i} href={url} target="_blank" rel="noreferrer" style={{position: 'relative'}}>
                        <img
                            src={url}
                            alt={alt}
                            style={{width: 80, height: 80, objectFit: 'cover', borderRadius: 6, opacity: removedAt ? 0.4 : 1}}
                        />
                        {removedAt && (
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                fontSize: 9, lineHeight: '12px', color: '#fff', background: 'rgba(0,0,0,0.65)',
                                borderRadius: '0 0 6px 6px', padding: '1px 3px', textAlign: 'center',
                            }}>
                                Removed {moment(removedAt?.toDate ? removedAt.toDate() : removedAt).format('DD MMM, HH:mm')}
                            </div>
                        )}
                    </a>
                );
            })}
        </div>
    );
}

// No dedicated status-history array exists on the order doc — every writer
// does a partial update, so earlier-stage evidence (receiptImages,
// deliveredAt) survives later transitions like cancellation. This
// synthesizes a timeline from the discrete timestamp fields that DO exist,
// so e.g. a cancelled order still shows whether it was ever shipped first.
// Note: deliveredAt is misleadingly named — it's stamped on the seller's
// FIRST receipt upload (orderStatus -> In_transit), not actual delivery.
function buildOrderTimeline(order: any, ledgerState: LedgerPayoutState | null): {label: string; date: any; note?: string}[] {
    const toMillis = (v: any) => v?.toMillis ? v.toMillis() : (v?.toDate ? v.toDate().getTime() : (v ? new Date(v).getTime() : null));
    const entries: {label: string; date: any; note?: string}[] = [];

    if (order.createdAt) entries.push({label: 'Order Placed', date: order.createdAt});

    if (order.deliveredAt) {
        const receiptCount = (order.receiptImages ?? []).length;
        const shipmentCount = (order.shipmentPhotos ?? []).length;
        const parts = [];
        if (receiptCount) parts.push(`${receiptCount} receipt photo${receiptCount > 1 ? 's' : ''}`);
        if (shipmentCount) parts.push(`${shipmentCount} shipment photo${shipmentCount > 1 ? 's' : ''}`);
        entries.push({
            label: 'Shipped (marked In Transit)',
            date: order.deliveredAt,
            note: parts.length ? `Seller uploaded ${parts.join(' + ')}` : undefined,
        });
    }

    if (order.confirmedAt) {
        entries.push({
            label: 'Delivery Confirmed',
            date: order.confirmedAt,
            note: order.autoConfirmed ? 'Auto-confirmed after 5 days — buyer never tapped confirm' : 'Buyer confirmed receipt',
        });
    }

    if (order.disputeOpenedAt) entries.push({label: 'Refund Dispute Opened', date: order.disputeOpenedAt});
    if (order.disputeResolvedAt) entries.push({label: 'Refund Dispute Resolved', date: order.disputeResolvedAt});

    if (ledgerState) {
        // clearsAt is when the money is *due* to clear, not when clearing began.
        // Once cleared, show the actual day (often earlier — the buyer confirmed);
        // until then, show the scheduled day.
        if (order.clearedAt) {
            const early = order.clearsAt && toMillis(order.clearsAt) > toMillis(order.clearedAt) + 60_000;
            entries.push({
                label: 'Cleared — Available to Seller',
                date: order.clearedAt,
                note: early ? `Cleared early (was scheduled for ${parseFirestoreDate(order.clearsAt)?.format('DD MMM YYYY')})` : undefined,
            });
        } else if (order.clearsAt && (order.orderStatus ?? '').toLowerCase() !== 'cancelled') {
            entries.push({label: 'Scheduled to Clear', date: order.clearsAt});
        }
        if (order.paidOutAt) entries.push({label: 'Paid Out to Seller', date: order.paidOutAt});
    }

    if (order.cancelledAt) {
        entries.push({
            label: 'Order Cancelled',
            date: order.cancelledAt,
            note: [order.cancelledBy && `by ${order.cancelledBy}`, order.cancellationReason].filter(Boolean).join(' — ') || undefined,
        });
    }

    if (order.adminOverride?.at) {
        entries.push({
            label: order.adminOverride.direction === 'seller' && order.adminOverride.initiatedPayment
                ? 'Admin Override — Paid Seller'
                : 'Admin Override Recorded',
            date: order.adminOverride.at,
            note: [
                order.adminOverride.byAdminEmail && `by ${order.adminOverride.byAdminEmail}`,
                order.adminOverride.payoutAmount != null && `MWK ${order.adminOverride.payoutAmount}`,
                order.adminOverride.reason,
            ].filter(Boolean).join(' — ') || undefined,
        });
    }

    entries.sort((a, b) => (toMillis(a.date) ?? 0) - (toMillis(b.date) ?? 0));
    return entries;
}

// An order "needs payout" when the buyer has paid and the order is confirmed, but
// the seller payout never completed.
//
// Ledger-system orders (ledgerVersion === 1) never write the legacy payoutStatus
// field at all — checking it here would false-flag every single currently-paid
// order as "needs payout", since payoutStatus is permanently empty for all of
// them. For those, only flag genuinely overdue ones: no paidOutAt yet, and past
// their clearsAt (should have cleared/paid by now and hasn't).
//
// Legacy (pre-ledger) orders keep the original check — payoutStatus empty
// (silently stuck, e.g. the old on-confirm Cloud Function crashed before writing
// a status) or 'failed'.
function needsPayout(r: {
    paymentStatusText: string; statusText: string; payoutStatus: string;
    ledgerVersion?: number; paidOutAt?: any; clearsAt?: any;
}): boolean {
    const paid = (r.paymentStatusText ?? '').toLowerCase() === 'successful';
    const confirmed = ['confirm', 'confirmed'].includes((r.statusText ?? '').toLowerCase());
    if (!paid || !confirmed) return false;

    if (r.ledgerVersion === 1) {
        if (r.paidOutAt) return false;
        const clearsAtMs = r.clearsAt?.toMillis ? r.clearsAt.toMillis() : null;
        return clearsAtMs != null && clearsAtMs < Date.now();
    }

    const payout = (r.payoutStatus ?? '').toLowerCase();
    return payout === '' || payout === 'failed';
}

// ─── Detail drawer ────────────────────────────────────────────────────────────

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                 padding:'10px 0', borderBottom:'1px solid #f3f3f3'}}>
        <span style={{color:'#888', fontSize:13}}>{label}</span>
        <span style={{fontWeight:500, fontSize:13, maxWidth:'60%', textAlign:'right'}}>{value ?? '—'}</span>
    </div>
);

const StatBox = ({label, value, sub}: {label: string; value: React.ReactNode; sub?: string}) => (
    <div style={{flex:1, background:'#fafafa', border:'1px solid #eee', borderRadius:10,
                 padding:'18px 20px', margin:'6px'}}>
        <div style={{color:'#888', fontSize:12, marginBottom:6}}>{label}</div>
        <div style={{fontSize:22, fontWeight:700, color:'#111'}}>{value}</div>
        {sub && <div style={{fontSize:12, color:'#aaa', marginTop:4}}>{sub}</div>}
    </div>
);

const TransactionDrawer = ({order, open, onClose}: {order: any; open: boolean; onClose: () => void}) => {
    const toast = useToast();
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [overrideReason, setOverrideReason] = useState('');
    const [initiatingPayment, setInitiatingPayment] = useState(false);

    // Whether the logged-in admin's role allows triggering a real payout.
    // Mirrors the server-side check in adminOverridePayoutToSeller — an
    // admin with no role/permissions assigned yet defaults to allowed
    // (every admin created before the role system existed), only an
    // explicit `permissions.transactions.payout === false` (content/support
    // roles, see ROLE_PERMISSIONS in Profile.tsx) disables it here.
    const [canPayout, setCanPayout] = useState(true);
    useEffect(() => {
        if (!open) return;
        const currentUid = getAuth(Firebase).currentUser?.uid;
        if (!currentUid) return;
        getDoc(doc(db, 'users', currentUid))
            .then((snap) => setCanPayout(snap.exists() ? snap.data().permissions?.transactions?.payout !== false : true))
            .catch(() => setCanPayout(true));
    }, [open]);

    // Wallet ledger entries for this order — sellerBalances/{sellerId}/ledger docs
    // either tagged with this exact orderId (sale/commission/vat/release, one order
    // each) or with orderIds containing it (a batched payout covering several
    // orders' net amounts at once — that's normal, not a bug; see Transactions.tsx
    // ledgerPayoutState comment). The payout entry's doc-id prefix (before "_payout")
    // is the same UUID the payment gateway shows as its "Charge ID" — that's the
    // correlation key for tracing a specific gateway transaction back to Shopped.
    const [ledgerEntries, setLedgerEntries] = useState<any[]>([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);

    useEffect(() => {
        if (!order?.sellerId || !order?.orderId || order.ledgerVersion !== 1) {
            setLedgerEntries([]);
            return;
        }
        let cancelled = false;
        setLedgerLoading(true);
        (async () => {
            try {
                const ledgerRef = collection(db, 'sellerBalances', order.sellerId, 'ledger');
                const [byOrderId, byOrderIds] = await Promise.all([
                    getDocs(query(ledgerRef, where('orderId', '==', order.orderId))),
                    getDocs(query(ledgerRef, where('orderIds', 'array-contains', order.orderId))),
                ]);
                if (cancelled) return;
                const map = new Map<string, any>();
                byOrderId.docs.forEach(d => map.set(d.id, {id: d.id, ...d.data()}));
                byOrderIds.docs.forEach(d => map.set(d.id, {id: d.id, ...d.data()}));
                const entries = Array.from(map.values()).sort((a, b) => {
                    const at = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                    const bt = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                    return at - bt;
                });
                setLedgerEntries(entries);
            } catch (e) {
                console.error('Failed to load ledger entries', e);
                if (!cancelled) setLedgerEntries([]);
            } finally {
                if (!cancelled) setLedgerLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [order?.sellerId, order?.orderId, order?.ledgerVersion]);

    if (!order) return null;

    const items: any[] = order.items ?? [];
    const attempts: any[] = order.payoutAttempts ?? [];
    const failedAttempts = attempts.filter(a => a.status === 'failed' || a.status === 'FAILED');
    const latestAttempt = attempts[attempts.length - 1];
    const ledgerState = ledgerPayoutState(order);
    // A cancelled/refund-accepted order's payoutStatus/payoutAttempts came from
    // payout_buyer_on_cancel.js (a buyer refund), not a seller payout — those two
    // flows write the same field names, so anything reading them has to branch
    // on which one actually happened to this order (same rule the table-row
    // builder above already applies for its payoutStatus/buyerPayoutStatus split).
    // EXCEPT once an admin override has actually paid the seller
    // (adminOverride.initiatedPayment) — at that point payoutStatus/netAmount
    // really do describe a seller payout again (adminOverridePayoutToSeller
    // overwrites them with the real payout outcome), so treat it as one, not
    // as a still-pending buyer refund.
    const wasOverriddenToSeller = order.adminOverride?.direction === 'seller' && order.adminOverride?.initiatedPayment === true;
    const isRefundFlow = !wasOverriddenToSeller
        && ((order.orderStatus ?? '').toLowerCase() === 'cancelled' || !!order.refundAcceptance);
    const canOverridePayout = isRefundFlow && !ledgerState && !order.adminOverride
        && ['failed', 'exhausted'].includes((order.payoutStatus ?? '').toLowerCase());
    const createdAtMoment = parseFirestoreDate(order.createdAt);
    const createdAtDisplay = createdAtMoment && createdAtMoment.isValid()
        ? createdAtMoment.format('DD MMM YYYY, HH:mm')
        : '—';

    const ORDER_STATUS_FLOW = ['pending', 'in_transit', 'confirmed', 'completed'];
    const currentIdx = ORDER_STATUS_FLOW.indexOf((order.orderStatus ?? '').toLowerCase());
    const nextStatus = currentIdx >= 0 && currentIdx < ORDER_STATUS_FLOW.length - 1
        ? ORDER_STATUS_FLOW[currentIdx + 1]
        : null;

    const handleStatusUpdate = async (newStatus: string) => {
        if (!order.orderId) return;
        setUpdatingStatus(true);
        try {
            await updateDoc(doc(db, 'orders', order.orderId), {orderStatus: newStatus});
            toast({position:'top', title:`Status updated to ${newStatus}`, status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally {
            setUpdatingStatus(false);
        }
    };

    const totalFee = latestAttempt
        ? (latestAttempt.originalAmount - latestAttempt.finalAmount)
        : null;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={820}
            title={null}
            bodyStyle={{padding:0, background:'#f7f8fc'}}
        >
            {/* ── Header ── */}
            <div style={{background:'#fff', padding:'20px 24px', borderBottom:'1px solid #eee'}}>
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap'}}>
                    <span style={{fontSize:11, color:'#aaa', letterSpacing:.4}}>ORDER ID</span>
                    <span style={{fontSize:11, color:'#555', fontFamily:'monospace'}}>
                        {order.orderId ?? '—'}
                    </span>
                    {order.orderNo && (
                        <span style={{fontSize:11, background:'#f0f0f0', borderRadius:4,
                                      padding:'1px 7px', color:'#555'}}>
                            #{order.orderNo}
                        </span>
                    )}
                </div>
                {/* Two clearly separated amounts */}
                <div style={{display:'flex', gap:24, flexWrap:'wrap', alignItems:'flex-end'}}>
                    <div>
                        <div style={{fontSize:11, color:'#aaa', marginBottom:3, letterSpacing:.4}}>BUYER PAID</div>
                        <div style={{fontSize:26, fontWeight:700, color:'#111'}}>
                            MWK {numberWithCommas(order.totalAmount ?? 0)}
                        </div>
                    </div>
                    {(order.netAmount ?? latestAttempt?.finalAmount) != null && (
                        <div>
                            <div style={{fontSize:11, color:'#aaa', marginBottom:3, letterSpacing:.4}}>SELLER RECEIVED</div>
                            <div style={{fontSize:22, fontWeight:700, color:'#29327b'}}>
                                MWK {numberWithCommas(order.netAmount ?? latestAttempt.finalAmount)}
                            </div>
                        </div>
                    )}
                    <div style={{display:'flex', gap:8, alignItems:'center', paddingBottom:4}}>
                        <Tag severity={statusSeverity(order.orderStatus ?? '')}
                             value={(order.orderStatus ?? 'Unknown').toUpperCase()} />
                        {order.paymentStatus && (
                            <Tag severity={statusSeverity(order.paymentStatus)}
                                 value={`Payment: ${order.paymentStatus}`} />
                        )}
                        {ledgerState ? (
                            <Tag severity={ledgerState.severity} value={`Payout: ${ledgerState.label}`} />
                        ) : order.payoutStatus && (
                            <Tag severity={statusSeverity(order.payoutStatus)}
                                 value={`${isRefundFlow ? 'Refund' : 'Payout'}: ${order.payoutStatus}`} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Body ── */}
            <div style={{display:'flex', gap:0, height:'calc(100vh - 150px)', overflow:'hidden'}}>

                {/* Left panel */}
                <div style={{width:280, minWidth:280, background:'#fff', borderRight:'1px solid #eee',
                              padding:'14px 18px', overflowY:'auto'}}>

                    {/* Buyer payment section */}
                    <div style={{fontSize:10, color:'#aaa', letterSpacing:.8, fontWeight:700,
                                 marginBottom:6, textTransform:'uppercase'}}>
                        Buyer Payment
                    </div>
                    <InfoRow label="Amount"
                             value={<b>MWK {numberWithCommas(order.totalAmount ?? 0)}</b>} />
                    <InfoRow label="Payment Method"
                             value={order.paymentMethods ?? order.paymentMethod ?? '—'} />
                    <InfoRow label="Gateway"
                             value={<span style={{textTransform:'capitalize'}}>{order.paymentProvider ?? '—'}</span>} />
                    <InfoRow label="Payment Status"
                             value={order.paymentStatus
                                 ? <Tag severity={statusSeverity(order.paymentStatus)} value={order.paymentStatus} />
                                 : '—'} />
                    <InfoRow label="Placed At" value={createdAtDisplay} />

                    <Divider style={{margin:'12px 0'}} />

                    {/* Seller payout / buyer refund section — these two flows write the
                        same legacy field names (payoutStatus/payoutAttempts), so the
                        label has to follow isRefundFlow or a cancelled order's stuck
                        refund reads as a failed seller payout. */}
                    <div style={{fontSize:10, color:'#aaa', letterSpacing:.8, fontWeight:700,
                                 marginBottom:6, textTransform:'uppercase'}}>
                        {!ledgerState && isRefundFlow ? 'Buyer Refund' : 'Seller Payout'}
                    </div>
                    {ledgerState ? (
                        <>
                            {order.netAmount != null && (
                                <InfoRow label="Net Paid to Seller"
                                         value={<b style={{color:'#22c55e'}}>
                                             MWK {numberWithCommas(order.netAmount)}
                                         </b>} />
                            )}
                            <InfoRow label="Payout Status"
                                     value={<Tag severity={ledgerState.severity} value={ledgerState.label} />} />
                            {ledgerState.date && (
                                <InfoRow label={ledgerState.label === 'Paid' ? 'Paid At' : 'Since'}
                                         value={parseFirestoreDate(ledgerState.date)?.format('DD MMM YYYY, HH:mm') ?? '—'} />
                            )}
                        </>
                    ) : wasOverriddenToSeller ? (
                        <>
                            {/* Real seller-commission breakdown from the override itself —
                                NOT the stale buyer-refund-fee numbers still sitting in
                                payoutAttempts from the exhausted refund attempt. */}
                            {order.adminOverride.grossAmount != null && (
                                <InfoRow label="Gross Amount" value={`MWK ${numberWithCommas(order.adminOverride.grossAmount)}`} />
                            )}
                            {order.adminOverride.chargeAmount > 0 && (
                                <InfoRow label="Service Fee Deducted" value={<span style={{color:'#ef4444'}}>
                                    − MWK {numberWithCommas(order.adminOverride.chargeAmount)}
                                </span>} />
                            )}
                            {order.adminOverride.payoutAmount != null && (
                                <InfoRow label="Net Paid to Seller" value={<b style={{color:'#22c55e'}}>
                                    MWK {numberWithCommas(order.adminOverride.payoutAmount)}
                                </b>} />
                            )}
                            <InfoRow label="Payout Status"
                                     value={order.payoutStatus
                                         ? <Tag severity={statusSeverity(order.payoutStatus)} value={order.payoutStatus} />
                                         : '—'} />
                            <InfoRow label="Admin Override" value={
                                <span style={{color:'#7c3aed'}}>Sent to seller instead of buyer refund</span>
                            } />
                        </>
                    ) : (
                        <>
                            {latestAttempt?.originalAmount != null && (
                                <InfoRow label={isRefundFlow ? 'Refund Base Amount' : 'Gross Amount'}
                                         value={`MWK ${numberWithCommas(latestAttempt.originalAmount)}`} />
                            )}
                            {totalFee !== null && totalFee > 0 && (
                                <InfoRow label={isRefundFlow ? 'Refund Fee Deducted' : 'Service Fee Deducted'}
                                         value={<span style={{color:'#ef4444'}}>
                                             − MWK {numberWithCommas(totalFee.toFixed(2))}
                                         </span>} />
                            )}
                            {latestAttempt?.finalAmount != null && (
                                <InfoRow label={isRefundFlow ? 'Net Refunded to Buyer' : 'Net Paid to Seller'}
                                         value={<b style={{color:'#22c55e'}}>
                                             MWK {numberWithCommas(latestAttempt.finalAmount)}
                                         </b>} />
                            )}
                            <InfoRow label={isRefundFlow ? 'Refund Method' : 'Payout Method'}
                                     value={latestAttempt?.correspondent
                                         ? latestAttempt.correspondent.replace('_MWI','').replace('_',' ')
                                         : '—'} />
                            <InfoRow label={isRefundFlow ? 'Refund Status' : 'Payout Status'}
                                     value={order.payoutStatus
                                         ? <Tag severity={statusSeverity(order.payoutStatus)} value={order.payoutStatus} />
                                         : '—'} />
                            {order.adminOverride && (
                                <InfoRow label="Admin Override" value={
                                    <span style={{color:'#7c3aed'}}>Sent to {order.adminOverride.direction} instead</span>
                                } />
                            )}
                        </>
                    )}

                    <Divider style={{margin:'12px 0'}} />

                    {/* Parties */}
                    <InfoRow label="Seller"
                             value={order.sellerId
                                 ? <Link to={`/shops/${order.sellerId}`} style={{color: '#2563eb'}}>
                                     {displayName(order.sellerName) ?? displayName(order.sellername) ?? order.sellerId}
                                 </Link>
                                 : '—'} />
                    <InfoRow label="Buyer"
                             value={order.buyerId
                                 ? <Link to={`/buyers/${order.buyerId}`} style={{color: '#2563eb'}}>
                                     {displayName(order.buyername) ?? displayName(order.buyerName) ?? order.buyerId}
                                 </Link>
                                 : '—'} />
                    {order.cancelledBy && (
                        <InfoRow label="Cancelled By" value={order.cancelledBy} />
                    )}
                    {order.cancellationReason && (
                        <InfoRow label="Reason" value={order.cancellationReason} />
                    )}
                </div>

                {/* Right panel */}
                <div style={{flex:1, overflowY:'auto', padding:'0 4px'}}>
                    <Tabs defaultActiveKey="details" style={{padding:'0 16px'}}>

                        {/* ── Payment (Buyer) ── */}
                        <TabPane tab="Payment" key="payment">
                            <div style={{padding:'12px 0'}}>
                                <div style={{display:'flex', flexWrap:'wrap'}}>
                                    <StatBox label="Buyer Paid"
                                        value={`MWK ${numberWithCommas(order.totalAmount ?? 0)}`} />
                                    <StatBox label="Payment Status"
                                        value={order.paymentStatus
                                            ? <Tag severity={statusSeverity(order.paymentStatus)} value={order.paymentStatus} />
                                            : '—'} />
                                    <StatBox label="Gateway"
                                        value={<span style={{textTransform:'capitalize'}}>{order.paymentProvider ?? '—'}</span>} />
                                </div>
                                <Divider style={{margin:'12px 0 8px'}}>Payment Details</Divider>
                                <div style={{background:'#fff', borderRadius:8, padding:'14px 16px', border:'1px solid #eee'}}>
                                    <InfoRow label="Payment Method"   value={order.paymentMethods ?? order.paymentMethod ?? '—'} />
                                    <InfoRow label="Gateway"          value={<span style={{textTransform:'capitalize'}}>{order.paymentProvider ?? '—'}</span>} />
                                    <InfoRow label="Payment Status"   value={order.paymentStatus
                                        ? <Tag severity={statusSeverity(order.paymentStatus)} value={order.paymentStatus} />
                                        : '—'} />
                                    <InfoRow label="Amount Paid"      value={`MWK ${numberWithCommas(order.totalAmount ?? 0)}`} />
                                    {order.amountPaid && order.amountPaid !== order.totalAmount && (
                                        <InfoRow label="Actual Amount Received" value={`MWK ${numberWithCommas(order.amountPaid)}`} />
                                    )}
                                    {order.paymentDepositId && (
                                        <InfoRow label="Deposit / Charge ID" value={
                                            <span style={{fontFamily:'monospace', fontSize:11}}>{order.paymentDepositId}</span>
                                        } />
                                    )}
                                    <InfoRow label="Buyer Phone"      value={order.buyerPaymentPhonenumber ?? '—'} />
                                    <InfoRow label="Placed At"        value={createdAtDisplay} />
                                </div>
                            </div>
                        </TabPane>

                        {/* ── Details ── */}
                        <TabPane tab="Details" key="details">
                            <div style={{padding:'12px 0'}}>
                                <div style={{fontWeight:600, marginBottom:10, color:'#333'}}>
                                    Order Items ({items.length})
                                </div>
                                {items.length === 0 ? (
                                    <p style={{color:'#aaa', fontSize:13}}>No item details available.</p>
                                ) : items.map((item: any, i: number) => {
                                    const Wrapper: any = item.productId ? Link : 'div';
                                    return (
                                    <Wrapper key={i} {...(item.productId ? {to: `/products/${item.productId}`} : {})} style={{
                                        display:'flex', alignItems:'center', gap:12,
                                        background:'#fff', borderRadius:8, padding:'10px 14px',
                                        marginBottom:8, border:'1px solid #eee',
                                        color:'inherit', textDecoration:'none',
                                        cursor: item.productId ? 'pointer' : 'default',
                                    }}>
                                        {item.imageUrl && (
                                            <img src={item.imageUrl} alt="" style={{
                                                width:48, height:48, objectFit:'cover', borderRadius:6, flexShrink:0
                                            }} />
                                        )}
                                        <div style={{flex:1}}>
                                            <div style={{fontWeight:600, fontSize:14, color: item.productId ? '#2563eb' : undefined}}>{item.productName ?? '—'}</div>
                                            <div style={{fontSize:12, color:'#888', marginTop:2}}>
                                                Qty: {item.quantity ?? 1}
                                                {item.price && ` · MWK ${numberWithCommas(item.price)}`}
                                                {item.selectedVariants && (
                                                    <span style={{marginLeft:8}}>
                                                        {Object.entries(item.selectedVariants).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{fontWeight:600, fontSize:14, color:'#29327b'}}>
                                            MWK {numberWithCommas((parseInt(item.quantity ?? 1) * parseFloat(item.price ?? 0)).toFixed(0))}
                                        </div>
                                    </Wrapper>
                                    );
                                })}

                                {/* Order status timeline — synthesized from the discrete timestamp
                                    fields that exist (no dedicated history array on the order doc);
                                    survives later transitions so a cancelled order still shows
                                    whether it was ever shipped/had a receipt first. */}
                                {(() => {
                                    const timeline = buildOrderTimeline(order, ledgerState);
                                    return timeline.length > 0 ? (
                                        <>
                                            <div style={{fontWeight:600, margin:'16px 0 8px', color:'#333'}}>Order Timeline</div>
                                            <div style={{background:'#fff', borderRadius:8, padding:'14px 16px', border:'1px solid #eee'}}>
                                                <Timeline>
                                                    {timeline.map((t, i) => (
                                                        <Timeline.Item key={i}>
                                                            <div style={{fontSize:13, fontWeight:600}}>{t.label}</div>
                                                            <div style={{fontSize:11, color:'#aaa'}}>{parseFirestoreDate(t.date)?.format('DD MMM YYYY, HH:mm') ?? '—'}</div>
                                                            {t.note && <div style={{fontSize:12, color:'#666', marginTop:2}}>{t.note}</div>}
                                                        </Timeline.Item>
                                                    ))}
                                                    <Timeline.Item color="blue">
                                                        <div style={{fontSize:13, fontWeight:600}}>Current: {order.orderStatus ?? '—'}</div>
                                                    </Timeline.Item>
                                                </Timeline>
                                            </div>
                                        </>
                                    ) : null;
                                })()}

                                {/* Shipment evidence — courier receipt + seller's product/shipment
                                    photos, if the seller ever uploaded any (survives cancellation). */}
                                {(order.receiptImages?.length > 0 || order.shipmentPhotos?.length > 0) && (
                                    <>
                                        <div style={{fontWeight:600, margin:'16px 0 8px', color:'#333'}}>Shipment Evidence</div>
                                        <div style={{background:'#fff', borderRadius:8, padding:'14px 16px', border:'1px solid #eee'}}>
                                            {order.receiptImages?.length > 0 && (
                                                <>
                                                    <div style={{fontSize:11, color:'#aaa'}}>COURIER RECEIPT</div>
                                                    <EvidenceThumbnails photos={order.receiptImages} alt="Courier receipt" />
                                                </>
                                            )}
                                            {order.shipmentPhotos?.length > 0 && (
                                                <>
                                                    <div style={{fontSize:11, color:'#aaa', marginTop: order.receiptImages?.length > 0 ? 14 : 0}}>PRODUCT / SHIPMENT PHOTOS</div>
                                                    <EvidenceThumbnails photos={order.shipmentPhotos} alt="Seller's shipment" />
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Delivery address if present */}
                                {order.deliveryAddress && (
                                    <>
                                        <div style={{fontWeight:600, margin:'16px 0 8px', color:'#333'}}>Delivery Address</div>
                                        <div style={{background:'#fff', borderRadius:8, padding:'12px 14px',
                                                     border:'1px solid #eee', fontSize:13, color:'#555'}}>
                                            {typeof order.deliveryAddress === 'string' ? order.deliveryAddress : (
                                                <>
                                                    {order.deliveryAddress.name && <div style={{fontWeight:600, color:'#333'}}>{order.deliveryAddress.name}</div>}
                                                    {order.deliveryAddress.address && <div>{order.deliveryAddress.address}</div>}
                                                    {order.deliveryAddress.city && <div>{order.deliveryAddress.city}</div>}
                                                    {order.deliveryAddress.phone && <div>{order.deliveryAddress.phone}</div>}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </TabPane>

                        {/* ── Payout (Seller) ── */}
                        <TabPane tab={!ledgerState && isRefundFlow ? "Refund (Buyer)" : "Payout (Seller)"} key="payout">
                            <div style={{padding:'12px 0'}}>

                                {ledgerState && (
                                    <>
                                        <div style={{display:'flex', flexWrap:'wrap'}}>
                                            <StatBox
                                                label="Payout Status"
                                                value={<Tag severity={ledgerState.severity} value={ledgerState.label} />}
                                            />
                                            <StatBox
                                                label="Net Amount"
                                                value={order.netAmount != null ? `MWK ${numberWithCommas(order.netAmount)}` : '—'}
                                            />
                                            <StatBox
                                                label="Clears At"
                                                value={order.clearsAt
                                                    ? (parseFirestoreDate(order.clearsAt)?.format('DD MMM YYYY, HH:mm') ?? '—')
                                                    : '—'}
                                            />
                                        </div>

                                        <Divider style={{margin:'12px 0 8px'}}>Wallet Ledger</Divider>
                                        <div style={{fontSize:12, color:'#888', marginBottom:10}}>
                                            The exact trail for this order's money — same source of truth the
                                            seller's app reads. A "payout" entry's ID (before the "_payout"
                                            suffix) is the same UUID the payment gateway shows as its "Charge
                                            ID" or "Reference" — use it to correlate a specific gateway
                                            transaction back here. One payout entry can cover several orders at
                                            once (whatever was sitting in the seller's available balance when
                                            it ran) — that's expected, not a bug.
                                        </div>
                                        {ledgerLoading ? (
                                            <p style={{color:'#aaa', fontSize:13}}>Loading ledger entries…</p>
                                        ) : ledgerEntries.length === 0 ? (
                                            <p style={{color:'#aaa', fontSize:13}}>
                                                No ledger entries yet — order hasn't been credited (seller hasn't
                                                uploaded delivery proof).
                                            </p>
                                        ) : (
                                            <div style={{background:'#fff', borderRadius:8, padding:'6px 16px',
                                                         border:'1px solid #eee', marginBottom:12}}>
                                                {ledgerEntries.map((entry) => (
                                                    <div key={entry.id} style={{padding:'10px 0', borderBottom:'1px solid #f3f3f3'}}>
                                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                            <span style={{fontWeight:600, fontSize:13, textTransform:'capitalize'}}>{entry.type}</span>
                                                            <span style={{fontWeight:600, fontSize:13, color:'#29327b'}}>
                                                                MWK {numberWithCommas(entry.amount ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div style={{fontSize:11, color:'#888', marginTop:2}}>
                                                            {entry.fromBucket && entry.toBucket && `${entry.fromBucket} → ${entry.toBucket} · `}
                                                            {entry.createdAt?.toDate ? moment(entry.createdAt.toDate()).format('DD MMM YYYY, HH:mm') : ''}
                                                        </div>
                                                        {entry.type === 'payout' && (
                                                            <div style={{fontSize:11, color:'#555', marginTop:4}}>
                                                                <div>
                                                                    Payout ID (= gateway Charge ID):{' '}
                                                                    <span style={{fontFamily:'monospace'}}>{entry.payoutId}</span>
                                                                </div>
                                                                {entry.orderIds?.length > 1 && (
                                                                    <div style={{marginTop:2}}>
                                                                        Bundled with {entry.orderIds.length - 1} other order
                                                                        {entry.orderIds.length - 1 > 1 ? 's' : ''}: {' '}
                                                                        <span style={{fontFamily:'monospace'}}>
                                                                            {entry.orderIds.filter((id: string) => id !== order.orderId).join(', ')}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <Divider style={{margin:'16px 0 8px'}}>Legacy Payout Attempts (pre-ledger)</Divider>
                                    </>
                                )}

                                {!ledgerState && isRefundFlow && (
                                    <div style={{fontSize:12, color:'#888', marginBottom:10, background:'#f7f8fc',
                                                 borderRadius:8, padding:'10px 14px', border:'1px solid #eee'}}>
                                        This order was cancelled — these are <b>buyer refund</b> attempts (not a
                                        seller payout). See the Actions tab to mark the refund done manually or
                                        override it to pay the seller instead.
                                    </div>
                                )}

                                {wasOverriddenToSeller && (
                                    <>
                                        <div style={{fontSize:12, color:'#7c3aed', marginBottom:10, background:'#f5f3ff',
                                                     borderRadius:8, padding:'10px 14px', border:'1px solid #ddd6fe'}}>
                                            The buyer refund below was overridden — this order's money was sent to
                                            the <b>seller</b> instead, for the reason given. The attempt history
                                            below is what the refund tried to do before it was overridden; it does
                                            <b> not</b> reflect what actually happened to the money.
                                        </div>
                                        <div style={{display:'flex', flexWrap:'wrap', marginBottom:12}}>
                                            <StatBox label="Paid to Seller"
                                                value={order.adminOverride.payoutAmount != null
                                                    ? `MWK ${numberWithCommas(order.adminOverride.payoutAmount)}` : '—'} />
                                            <StatBox label="Payout Status"
                                                value={order.payoutStatus
                                                    ? <Tag severity={statusSeverity(order.payoutStatus)} value={order.payoutStatus} />
                                                    : '—'} />
                                            <StatBox label="Overridden By"
                                                value={<span style={{fontSize:14}}>{order.adminOverride.byAdminEmail ?? '—'}</span>} />
                                        </div>
                                        <Divider style={{margin:'12px 0 8px'}}>Superseded Refund Attempt History</Divider>
                                    </>
                                )}

                                {!ledgerState && !wasOverriddenToSeller && (
                                    <div style={{display:'flex', flexWrap:'wrap'}}>
                                        <StatBox
                                            label={isRefundFlow ? 'Refund Status' : 'Payout Status'}
                                            value={order.payoutStatus
                                                ? <Tag severity={statusSeverity(order.payoutStatus)} value={order.payoutStatus} />
                                                : '—'}
                                        />
                                        <StatBox
                                            label="Attempts"
                                            value={`${attempts.length} attempt${attempts.length !== 1 ? 's' : ''}`}
                                        />
                                        <StatBox
                                            label="Failures"
                                            value={failedAttempts.length === 0
                                                ? <span style={{color:'#22c55e'}}>0 failures</span>
                                                : <span style={{color:'#ef4444'}}>{failedAttempts.length} failure{failedAttempts.length > 1 ? 's' : ''}</span>
                                            }
                                        />
                                    </div>
                                )}

                                {latestAttempt && (
                                    <>
                                        <Divider style={{margin:'12px 0 8px'}}>{wasOverriddenToSeller ? 'Latest Refund Attempt (superseded)' : 'Latest Attempt'}</Divider>
                                        <div style={{background:'#fff', borderRadius:8, padding:'14px 16px',
                                                     border:'1px solid #eee', marginBottom:12}}>
                                            <InfoRow label={isRefundFlow ? 'Refund ID' : 'Payout ID'} value={<span style={{fontFamily:'monospace', fontSize:11}}>{latestAttempt.payoutId}</span>} />
                                            <InfoRow label="Status" value={<Tag severity={statusSeverity(latestAttempt.status)} value={latestAttempt.status} />} />
                                            <InfoRow label="Correspondent" value={latestAttempt.correspondent ?? '—'} />
                                            <InfoRow label="Phone" value={latestAttempt.recipientPhoneNumber ?? '—'} />
                                            <InfoRow label="Original Amount" value={`MWK ${numberWithCommas(latestAttempt.originalAmount ?? 0)}`} />
                                            <InfoRow label="Final Amount" value={`MWK ${numberWithCommas(latestAttempt.finalAmount ?? 0)}`} />
                                            {latestAttempt.errorMessage && (
                                                <InfoRow label="Error" value={
                                                    <span style={{color:'#ef4444', fontSize:12}}>{latestAttempt.errorMessage}</span>
                                                } />
                                            )}
                                            <InfoRow label="At" value={latestAttempt.createdAt
                                                ? moment(latestAttempt.createdAt).format('DD MMM YYYY, HH:mm')
                                                : '—'} />
                                        </div>
                                    </>
                                )}

                                {attempts.length > 1 && (
                                    <>
                                        <Divider style={{margin:'12px 0 8px'}}>All Attempts</Divider>
                                        <Timeline>
                                            {attempts.map((a: any, i: number) => (
                                                <Timeline.Item
                                                    key={i}
                                                    color={a.status === 'successful' || a.status === 'COMPLETED' ? 'green'
                                                        : a.status === 'failed' || a.status === 'FAILED' ? 'red'
                                                        : 'orange'}
                                                >
                                                    <div style={{fontSize:13}}>
                                                        <b>Attempt {a.attemptNo ?? i + 1}</b> — {a.status}
                                                        {a.errorMessage && <span style={{color:'#ef4444', marginLeft:8}}>{a.errorMessage}</span>}
                                                        <div style={{color:'#aaa', fontSize:11}}>
                                                            {a.createdAt ? moment(a.createdAt).format('DD MMM YYYY, HH:mm') : ''}
                                                        </div>
                                                    </div>
                                                </Timeline.Item>
                                            ))}
                                        </Timeline>
                                    </>
                                )}

                                {attempts.length === 0 && (
                                    <p style={{color:'#aaa', fontSize:13, marginTop:12}}>
                                        {ledgerState
                                            ? 'No legacy (pre-ledger) payout attempts for this order — see Wallet Ledger above.'
                                            : 'No payout attempts recorded.'}
                                    </p>
                                )}
                            </div>
                        </TabPane>

                        {/* ── Refund ── */}
                        <TabPane tab="Refund" key="refund">
                            <div style={{padding:'12px 0'}}>
                                {!order.cancelledBy && order.orderStatus?.toLowerCase() !== 'cancelled' ? (
                                    <p style={{color:'#aaa', fontSize:13}}>No refund on this order.</p>
                                ) : (
                                    <>
                                        <div style={{display:'flex', flexWrap:'wrap'}}>
                                            <StatBox
                                                label="Refund Status"
                                                value={order.refundStatus
                                                    ? <Tag severity={statusSeverity(order.refundStatus)} value={order.refundStatus} />
                                                    : <Tag severity="warning" value="pending" />}
                                            />
                                            <StatBox
                                                label="Cancelled By"
                                                value={order.cancelledBy ?? '—'}
                                            />
                                            <StatBox
                                                label="Amount"
                                                value={`MWK ${numberWithCommas(order.totalAmount ?? 0)}`}
                                            />
                                        </div>
                                        <Divider style={{margin:'12px 0 8px'}}>Cancellation Details</Divider>
                                        <div style={{background:'#fff', borderRadius:8, padding:'14px 16px',
                                                     border:'1px solid #eee'}}>
                                            <InfoRow label="Reason" value={order.cancellationReason ?? order.cancelReason ?? '—'} />
                                            <InfoRow label="Refund Status" value={order.refundStatus ?? '—'} />
                                            {order.refundPayoutId && (
                                                <InfoRow label="Refund Payout ID" value={
                                                    <span style={{fontFamily:'monospace', fontSize:11}}>{order.refundPayoutId}</span>
                                                } />
                                            )}
                                            {order.sellerCancelDebtId && (
                                                <InfoRow label="Seller Debt ID" value={
                                                    <span style={{fontFamily:'monospace', fontSize:11}}>{order.sellerCancelDebtId}</span>
                                                } />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </TabPane>

                        {/* ── Actions ── */}
                        <TabPane tab="Actions" key="actions">
                            <div style={{padding:'12px 0'}}>

                                {/* Status snapshot */}
                                <div style={{background:'#fff', borderRadius:8, padding:'12px 16px',
                                             border:'1px solid #eee', marginBottom:16}}>
                                    <InfoRow label="Order"   value={<Tag severity={statusSeverity(order.orderStatus ?? '')} value={order.orderStatus ?? '—'} />} />
                                    <InfoRow label="Payment (buyer)"  value={order.paymentStatus ? <Tag severity={statusSeverity(order.paymentStatus)} value={order.paymentStatus} /> : '—'} />
                                    <InfoRow label={!ledgerState && isRefundFlow ? 'Refund (buyer)' : 'Payout (seller)'} value={ledgerState
                                        ? <Tag severity={ledgerState.severity} value={ledgerState.label} />
                                        : (order.payoutStatus ? <Tag severity={statusSeverity(order.payoutStatus)} value={order.payoutStatus} /> : '—')} />
                                </div>

                                {/* ── Order status flow ── */}
                                <div style={{fontWeight:600, marginBottom:8, color:'#333'}}>Order Status</div>
                                <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:18}}>
                                    {nextStatus && (
                                        <NButton size="sm" color="primary" disabled={updatingStatus}
                                                 onPress={() => handleStatusUpdate(nextStatus)}>
                                            Mark as {nextStatus.replace('_', ' ')}
                                        </NButton>
                                    )}
                                    {!['cancelled', 'completed'].includes((order.orderStatus ?? '').toLowerCase()) && (
                                        <NButton size="sm" color="error" flat disabled={updatingStatus}
                                                 onPress={() => handleStatusUpdate('cancelled')}>
                                            Cancel Order
                                        </NButton>
                                    )}
                                    {(order.orderStatus ?? '').toLowerCase() === 'completed' && (
                                        <span style={{color:'#22c55e', fontSize:13, alignSelf:'center'}}>✓ Order completed</span>
                                    )}
                                </div>

                                <Divider style={{margin:'6px 0 14px'}} />

                                {/* ── Payout actions ── */}
                                <div style={{fontWeight:600, marginBottom:6, color:'#333'}}>Seller Payout</div>
                                {!ledgerState && isRefundFlow ? (
                                    <div style={{fontSize:12, color:'#888', marginBottom:18, background:'#f7f8fc',
                                                 borderRadius:8, padding:'10px 14px', border:'1px solid #eee'}}>
                                        This order was cancelled — the payoutStatus/payoutAttempts data here is
                                        actually a <b>buyer refund</b> attempt, not a seller payout (see the Refund
                                        tab). Use <b>Buyer Refund</b> below, or the Admin Override, instead of
                                        marking a payout done — this order should never be advanced to
                                        &quot;Completed&quot;.
                                    </div>
                                ) : ledgerState ? (
                                    <div style={{fontSize:12, color:'#888', marginBottom:18, background:'#f7f8fc',
                                                 borderRadius:8, padding:'10px 14px', border:'1px solid #eee'}}>
                                        This order is on the wallet ledger system — payouts are handled
                                        automatically (mobile money: within the hour the order clears; bank
                                        transfer: weekly batch once the seller's balance clears the minimum,
                                        or an automatic mobile-money fallback after 7 days). See the{' '}
                                        <b>Wallet Ledger</b> section on the Payout (Seller) tab for the exact
                                        trail. There's no manual "trigger" action here — the old on-confirm
                                        Cloud Function this used to call has been removed, and re-flipping
                                        orderStatus could double-credit the seller's balance if this order was
                                        already paid under the old system. If a payout genuinely looks stuck,
                                        check <code>sellerBalances/{'{sellerId}'}</code> and its ledger
                                        subcollection directly, or escalate to engineering.
                                    </div>
                                ) : (
                                    <>
                                        <div style={{fontSize:12, color:'#888', marginBottom:10}}>
                                            Legacy pre-ledger order. Payout was handled by a Cloud Function that
                                            has since been replaced by the wallet ledger system — there's no
                                            working automated retry for this order anymore.
                                        </div>
                                        <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:18}}>
                                            {/* Mark payout as manually completed — legacy orders only. Does not
                                                touch sellerBalances/ledger, so only use this for orders that never
                                                went through the ledger system in the first place. */}
                                            <NButton size="sm" color="success" flat disabled={updatingStatus}
                                                     onPress={async () => {
                                                         setUpdatingStatus(true);
                                                         try {
                                                             await updateDoc(doc(db, 'orders', order.orderId), {
                                                                 payoutStatus: 'successful',
                                                                 orderStatus: 'Completed',
                                                             });
                                                             toast({position:'top', title:'Payout marked successful', status:'success', duration:2000, isClosable:true});
                                                         } catch(e: any) {
                                                             toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                         } finally { setUpdatingStatus(false); }
                                                     }}>
                                                Mark Payout Done
                                            </NButton>
                                        </div>
                                    </>
                                )}

                                <Divider style={{margin:'6px 0 14px'}} />

                                {/* ── Refund actions ── */}
                                <div style={{fontWeight:600, marginBottom:6, color:'#333'}}>Buyer Refund</div>
                                <div style={{fontSize:12, color:'#888', marginBottom:10}}>
                                    Cancelling a paid order triggers an automatic refund to the buyer
                                    (works for orders placed within 100 minutes, or seller-cancelled orders).
                                    For older orders, use <b>Mark Refund Done</b> after processing manually.
                                </div>
                                <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:18}}>
                                    {/* Cancel + refund: triggers payout_buyer_on_cancel CF */}
                                    {!['cancelled', 'completed'].includes((order.orderStatus ?? '').toLowerCase()) &&
                                     (order.paymentStatus ?? '').toLowerCase() === 'successful' && (
                                        <NButton size="sm" color="error" flat disabled={updatingStatus}
                                                 onPress={async () => {
                                                     setUpdatingStatus(true);
                                                     try {
                                                         await updateDoc(doc(db, 'orders', order.orderId), {
                                                             orderStatus: 'cancelled',
                                                             cancelledBy: 'admin',
                                                             cancellationReason: 'Admin-initiated refund',
                                                         });
                                                         toast({position:'top', title:'Order cancelled — refund initiated', status:'success', duration:2500, isClosable:true});
                                                     } catch(e: any) {
                                                         toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                     } finally { setUpdatingStatus(false); }
                                                 }}>
                                            Cancel &amp; Refund Buyer
                                        </NButton>
                                    )}
                                    {/* Mark refund as done manually */}
                                    <NButton size="sm" color="default" flat disabled={updatingStatus}
                                             onPress={async () => {
                                                 setUpdatingStatus(true);
                                                 try {
                                                     await updateDoc(doc(db, 'orders', order.orderId), {
                                                         refundStatus: 'successful',
                                                     });
                                                     toast({position:'top', title:'Refund marked as done', status:'success', duration:2000, isClosable:true});
                                                 } catch(e: any) {
                                                     toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                 } finally { setUpdatingStatus(false); }
                                             }}>
                                        Mark Refund Done
                                    </NButton>
                                </div>

                                {/* ── Admin override: reroute a stuck refund to the seller ── */}
                                {(canOverridePayout || order.adminOverride) && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:6, color:'#7c3aed'}}>Admin Override</div>
                                        {order.adminOverride && (
                                            <div style={{fontSize:12, color:'#555', marginBottom:order.adminOverride.initiatedPayment ? 18 : 10,
                                                         background:'#f5f3ff', borderRadius:8, padding:'10px 14px', border:'1px solid #ddd6fe'}}>
                                                Overridden to pay the <b>{order.adminOverride.direction}</b> instead —
                                                &quot;{order.adminOverride.reason}&quot;
                                                {order.adminOverride.byAdminEmail && <> (by {order.adminOverride.byAdminEmail})</>}
                                                <div style={{marginTop:4}}>
                                                    {order.adminOverride.initiatedPayment
                                                        ? <>Payment was initiated through the system (payout status: <b>{order.payoutStatus ?? 'pending'}</b>).</>
                                                        : 'Recorded only — no payment has been sent through the system yet. Use Initiate Payment below to actually send it, or confirm it was already sent manually.'}
                                                </div>
                                            </div>
                                        )}
                                        {!order.adminOverride?.initiatedPayment && (
                                            <>
                                                {!order.adminOverride && (
                                                    <div style={{fontSize:12, color:'#888', marginBottom:10}}>
                                                        The automated buyer refund has exhausted its retries. Use this only
                                                        if the buyer and seller resolved this outside the app (e.g. the seller
                                                        already delivered/settled directly) and the held money should go to
                                                        the seller instead of back to the buyer.
                                                    </div>
                                                )}
                                                {!canPayout && (
                                                    <div style={{fontSize:12, color:'#b91c1c', marginBottom:10, background:'#fef2f2',
                                                                 borderRadius:8, padding:'8px 12px', border:'1px solid #fca5a5'}}>
                                                        Your admin role doesn&apos;t have payout permission — Initiate Payment is disabled.
                                                        Ask a Super Admin, Operations, or Finance admin to do this instead.
                                                    </div>
                                                )}
                                                <Input
                                                    placeholder="Reason (required, shown in the audit trail)"
                                                    value={overrideReason}
                                                    onChange={(e: any) => setOverrideReason(e.target.value)}
                                                    style={{marginBottom:10, maxWidth:420}}
                                                />
                                                <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:6}}>
                                                    <NButton size="sm" color="secondary"
                                                             disabled={!canPayout || initiatingPayment || updatingStatus || !overrideReason.trim()}
                                                             onPress={async () => {
                                                                 setInitiatingPayment(true);
                                                                 try {
                                                                     const result: any = await adminOverridePayoutToSeller({
                                                                         orderId: order.orderId,
                                                                         reason: overrideReason.trim(),
                                                                     });
                                                                     const status = result.data?.status;
                                                                     toast({
                                                                         position:'top',
                                                                         title: status === 'successful' ? 'Paid — sent to seller successfully'
                                                                             : status === 'pending' ? 'Payment initiated — still resolving with the provider'
                                                                             : `Payment attempt finished with status: ${status}`,
                                                                         status: status === 'successful' || status === 'pending' ? 'success' : 'warning',
                                                                         duration: 3500, isClosable: true,
                                                                     });
                                                                     setOverrideReason('');
                                                                 } catch(e: any) {
                                                                     toast({position:'top', title: e.message, status:'error', duration:4000, isClosable:true});
                                                                 } finally { setInitiatingPayment(false); }
                                                             }}>
                                                        {initiatingPayment ? 'Sending…' : 'Initiate Payment (Pay Seller Now)'}
                                                    </NButton>
                                                    {!order.adminOverride && (
                                                        <NButton size="sm" color="secondary" flat
                                                                 disabled={initiatingPayment || updatingStatus || !overrideReason.trim()}
                                                                 onPress={async () => {
                                                                     setUpdatingStatus(true);
                                                                     try {
                                                                         const admin = getAuth(Firebase).currentUser;
                                                                         await updateDoc(doc(db, 'orders', order.orderId), {
                                                                             adminOverride: {
                                                                                 direction: 'seller',
                                                                                 reason: overrideReason.trim(),
                                                                                 byAdminUid: admin?.uid ?? null,
                                                                                 byAdminEmail: admin?.email ?? null,
                                                                                 at: serverTimestamp(),
                                                                                 initiatedPayment: false,
                                                                             },
                                                                             payoutStatus: 'overridden',
                                                                             refundStatus: 'overridden',
                                                                             updatedAt: serverTimestamp(),
                                                                         });
                                                                         toast({position:'top', title:'Marked as paid to seller (record only)', status:'success', duration:2500, isClosable:true});
                                                                         setOverrideReason('');
                                                                     } catch(e: any) {
                                                                         toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                                     } finally { setUpdatingStatus(false); }
                                                                 }}>
                                                            Mark as Paid Manually (Record Only)
                                                        </NButton>
                                                    )}
                                                </div>
                                                <div style={{fontSize:11, color:'#aaa', marginBottom:18}}>
                                                    <b>Initiate Payment</b> actually sends the money now, through the
                                                    same system every seller payout uses.
                                                    {!order.adminOverride && <> <b>Mark as Paid Manually</b> only
                                                    records the decision — use it if you already sent the money
                                                    yourself outside the app.</>}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                <Divider style={{margin:'6px 0 14px'}} />

                                {/* ── Payment overrides ── */}
                                <div style={{fontWeight:600, marginBottom:6, color:'#333'}}>Payment Overrides</div>
                                <div style={{fontSize:12, color:'#888', marginBottom:10}}>
                                    Use when you've verified payment outside the system (e.g. manual bank transfer).
                                </div>
                                <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:6}}>
                                    <NButton size="sm" color="primary" flat disabled={updatingStatus}
                                             onPress={async () => {
                                                 setUpdatingStatus(true);
                                                 try {
                                                     await updateDoc(doc(db, 'orders', order.orderId), {paymentStatus: 'successful'});
                                                     toast({position:'top', title:'Payment marked successful', status:'success', duration:2000, isClosable:true});
                                                 } catch(e: any) {
                                                     toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                 } finally { setUpdatingStatus(false); }
                                             }}>
                                        Mark Payment Verified
                                    </NButton>
                                    <NButton size="sm" color="error" flat disabled={updatingStatus}
                                             onPress={async () => {
                                                 setUpdatingStatus(true);
                                                 try {
                                                     await updateDoc(doc(db, 'orders', order.orderId), {paymentStatus: 'failed'});
                                                     toast({position:'top', title:'Payment marked failed', status:'warning', duration:2000, isClosable:true});
                                                 } catch(e: any) {
                                                     toast({position:'top', title:e.message, status:'error', duration:3000, isClosable:true});
                                                 } finally { setUpdatingStatus(false); }
                                             }}>
                                        Mark Payment Failed
                                    </NButton>
                                </div>

                                <Divider style={{margin:'14px 0 10px'}}>IDs</Divider>
                                <div style={{background:'#fff', borderRadius:8, padding:'12px 16px', border:'1px solid #eee'}}>
                                    <InfoRow label="Order ID"  value={<span style={{fontFamily:'monospace', fontSize:11}}>{order.orderId ?? '—'}</span>} />
                                    <InfoRow label="Order No"  value={order.orderNo ?? '—'} />
                                    <InfoRow label="Seller ID" value={<span style={{fontFamily:'monospace', fontSize:11}}>{order.sellerId ?? '—'}</span>} />
                                    <InfoRow label="Buyer ID"  value={<span style={{fontFamily:'monospace', fontSize:11}}>{order.buyerId ?? '—'}</span>} />
                                    {order.paymentDepositId && (
                                        <InfoRow label="Deposit ID" value={<span style={{fontFamily:'monospace', fontSize:11}}>{order.paymentDepositId}</span>} />
                                    )}
                                </div>
                            </div>
                        </TabPane>

                    </Tabs>
                </div>
            </div>
        </Drawer>
    );
};

// ─── Column manager ───────────────────────────────────────────────────────────

interface ColConfig { key: string; label: string; visible: boolean; }

const DEFAULT_COLS: ColConfig[] = [
    { key: 'orderNo',        label: 'Order No',            visible: true  },
    { key: 'seller',         label: 'Seller',              visible: true  },
    { key: 'buyer',          label: 'Buyer',               visible: true  },
    { key: 'product',        label: 'Product',             visible: true  },
    { key: 'rawAmount',      label: 'Buyer Paid',          visible: true  },
    { key: 'status',         label: 'Order Status',        visible: true  },
    { key: 'paymentStatus',  label: 'Payment (Buyer)',     visible: true  },
    { key: 'payoutStatus',   label: 'Payout (Seller)',     visible: true  },
    { key: 'buyerPayoutStatus', label: 'Refund (Buyer)',   visible: true  },
    { key: 'paymentProvider',label: 'Gateway',             visible: true  },
    { key: 'paymentMethod',  label: 'Payment Method',      visible: false },
    { key: 'date',           label: 'Date',                visible: true  },
    { key: 'updatedAt',      label: 'Last Modified',       visible: true  },
];

const ColumnManager = ({cols, onChange}: {cols: ColConfig[]; onChange: (c: ColConfig[]) => void}) => {
    const dragIdx = useRef<number | null>(null);

    const toggleVisible = (key: string) => {
        onChange(cols.map(c => c.key === key ? {...c, visible: !c.visible} : c));
    };

    const onDragStart = (i: number) => { dragIdx.current = i; };
    const onDragOver  = (e: React.DragEvent, i: number) => {
        e.preventDefault();
        if (dragIdx.current === null || dragIdx.current === i) return;
        const next = [...cols];
        const [moved] = next.splice(dragIdx.current, 1);
        next.splice(i, 0, moved);
        dragIdx.current = i;
        onChange(next);
    };
    const onDragEnd = () => { dragIdx.current = null; };

    const content = (
        <div style={{width: 220}}>
            <div style={{fontSize:11, color:'#aaa', marginBottom:8, letterSpacing:.5}}>
                DRAG TO REORDER · CHECK TO SHOW
            </div>
            {cols.map((col, i) => (
                <div
                    key={col.key}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={e => onDragOver(e, i)}
                    onDragEnd={onDragEnd}
                    style={{
                        display:'flex', alignItems:'center', gap:8,
                        padding:'6px 4px', borderRadius:6, cursor:'grab',
                        userSelect:'none',
                        background: dragIdx.current === i ? '#f0f4ff' : 'transparent',
                    }}
                >
                    <span style={{color:'#bbb', fontSize:14, cursor:'grab'}}>⠿</span>
                    <Checkbox
                        checked={col.visible}
                        onChange={() => toggleVisible(col.key)}
                    />
                    <span style={{fontSize:13}}>{col.label}</span>
                </div>
            ))}
            <div style={{borderTop:'1px solid #f0f0f0', marginTop:8, paddingTop:8,
                         display:'flex', justifyContent:'space-between'}}>
                <button style={{fontSize:11, color:'#29327b', background:'none',
                                border:'none', cursor:'pointer', padding:0}}
                        onClick={() => onChange(cols.map(c => ({...c, visible: true})))}>
                    Show all
                </button>
                <button style={{fontSize:11, color:'#aaa', background:'none',
                                border:'none', cursor:'pointer', padding:0}}
                        onClick={() => onChange(DEFAULT_COLS)}>
                    Reset
                </button>
            </div>
        </div>
    );

    return (
        <Popover content={content} title={null} trigger="click" placement="bottomRight">
            <button style={{
                display:'flex', alignItems:'center', gap:6, fontSize:13,
                padding:'5px 12px', borderRadius:8, border:'1px solid #ddd',
                background:'#fff', cursor:'pointer', color:'#444',
            }}>
                <MDBIcon icon="columns" /> Columns
            </button>
        </Popover>
    );
};

// ─── Main Transactions page ───────────────────────────────────────────────────

const ORDER_STATUSES = ['pending', 'in_transit', 'confirmed', 'completed', 'cancelled'];
const PAYOUT_STATUSES = ['pending', 'successful', 'failed', 'exhausted', 'overridden'];
const PROVIDERS = ['paychangu', 'pawapay'];

const Transactions = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [colConfigs, setColConfigs] = useState<ColConfig[]>(DEFAULT_COLS);

    // All rows from Firestore (never mutated after load)
    const [allRows, setAllRows] = useState<DataType[]>([]);

    // Filter / search state
    const [searchText, setSearchText] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [payoutStatusFilter, setPayoutStatusFilter] = useState('all');
    const [refundStatusFilter, setRefundStatusFilter] = useState('all');
    const [needsPayoutOnly, setNeedsPayoutOnly] = useState(false);
    const [payoutTroubleOnly, setPayoutTroubleOnly] = useState(false);
    const [refundTroubleOnly, setRefundTroubleOnly] = useState(false);
    const [providerFilter, setProviderFilter] = useState('all');
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

    const [snapshot, loading] = useCollection(
        collection(db, 'orders'),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );
    const [productSnapshot] = useCollection(
        collection(db, 'products'),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );

    // Build rows whenever Firestore data changes
    useEffect(() => {
        if (!snapshot) return;
        const productMap: Record<string, string> = {};
        productSnapshot?.docs.forEach((d: any) => {
            const p = d.data();
            if (p.productId) productMap[p.productId] = p.name;
        });

        const rows: DataType[] = snapshot.docs.map((docSnap: any) => {
            const item = docSnap.data();
            const orderStatus: string = item.orderStatus ?? '';
            const productName = (item.items && item.items.length > 0)
                ? item.items[0].productName
                : (productMap[item.productId] ?? '');

            // Handle Firestore Timestamp, JS Date, number, or string
            const m = parseFirestoreDate(item.createdAt);
            const dateMs = m && m.isValid() ? m.valueOf() : 0;
            const um = parseFirestoreDate(item.updatedAt);
            const updatedAtMs = um && um.isValid() ? um.valueOf() : 0;

            // A cancelled/refund-accepted order's payoutStatus/payoutAttempts came from
            // payout_buyer_on_cancel.js (a buyer refund), not a seller payout — those two
            // flows write the same fields, so read them as one or the other based on which
            // actually happened to this order, never both. Unless an admin override has
            // actually paid the seller — then payoutStatus is a real seller payout again.
            const wasOverriddenToSeller = item.adminOverride?.direction === 'seller' && item.adminOverride?.initiatedPayment === true;
            const isRefundFlow = !wasOverriddenToSeller
                && (orderStatus.toLowerCase() === 'cancelled' || !!item.refundAcceptance);

            return {
                key: docSnap.id,
                orderId: item.orderId ?? docSnap.id,
                orderNo: String(item.orderNo ?? ''),
                seller: displayName(item.sellerName) ?? displayName(item.sellername) ?? '',
                buyer: displayName(item.buyername) ?? displayName(item.buyerName) ?? '',
                amount: `MWK ${numberWithCommas(item.totalAmount ?? 0)}`,
                rawAmount: parseFloat(item.totalAmount ?? 0),
                product: productName,
                statusText: orderStatus,
                status: <Tag severity={statusSeverity(orderStatus)} value={orderStatus || '—'} />,
                paymentStatusText: item.paymentStatus ?? '',
                payoutStatus: isRefundFlow ? '' : (item.payoutStatus ?? ''),
                buyerPayoutStatus: isRefundFlow ? (item.payoutStatus ?? '') : '',
                ledgerVersion: item.ledgerVersion,
                paidOutAt: item.paidOutAt,
                clearsAt: item.clearsAt,
                clearedAt: item.clearedAt,
                paymentProvider: item.paymentProvider ?? '',
                paymentMethod: item.paymentMethods ?? item.paymentMethod ?? '',
                dateMs,
                date: m && m.isValid() ? m.format('DD MMM YYYY') : '',
                updatedAtMs,
                updatedAt: um && um.isValid() ? um.format('DD MMM YYYY, HH:mm') : '',
                raw: {...item, orderId: item.orderId ?? docSnap.id},
            };
        });

        setAllRows(rows);
    }, [snapshot, productSnapshot]);

    // Deep link from other pages (e.g. Disputes) via ?orderId= — open that
    // order's drawer once its row shows up, then drop the param so a manual
    // close doesn't immediately re-open it on the next render.
    useEffect(() => {
        const targetOrderId = searchParams.get('orderId');
        if (!targetOrderId || allRows.length === 0) return;
        const row = allRows.find((r) => r.orderId === targetOrderId || r.key === targetOrderId);
        if (row) {
            setSelectedOrder(row.raw);
            setDrawerOpen(true);
        }
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('orderId');
            return next;
        }, {replace: true});
    }, [allRows, searchParams, setSearchParams]);

    // Apply filters + search + sort reactively
    const displayRows = useMemo(() => {
        let rows = allRows;

        // Search
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            rows = rows.filter(r =>
                r.orderId.toLowerCase().includes(q) ||
                r.orderNo.toLowerCase().includes(q) ||
                r.seller.toLowerCase().includes(q) ||
                r.buyer.toLowerCase().includes(q) ||
                r.product.toLowerCase().includes(q) ||
                r.paymentMethod.toLowerCase().includes(q) ||
                r.paymentProvider.toLowerCase().includes(q) ||
                r.statusText.toLowerCase().includes(q) ||
                (r.paymentStatusText?.toLowerCase() ?? '').includes(q) ||
                r.payoutStatus.toLowerCase().includes(q) ||
                r.buyerPayoutStatus.toLowerCase().includes(q) ||
                r.date.toLowerCase().includes(q) ||
                r.updatedAt.toLowerCase().includes(q)
            );
        }

        // Order status filter
        if (orderStatusFilter !== 'all') {
            rows = rows.filter(r => r.statusText.toLowerCase() === orderStatusFilter);
        }

        // Payout status filter
        if (payoutStatusFilter !== 'all') {
            rows = rows.filter(r => r.payoutStatus.toLowerCase() === payoutStatusFilter);
        }

        // Refund (buyer) status filter
        if (refundStatusFilter !== 'all') {
            rows = rows.filter(r => r.buyerPayoutStatus.toLowerCase() === refundStatusFilter);
        }

        // "Needs payout" filter — paid & confirmed but payout empty or failed
        if (needsPayoutOnly) {
            rows = rows.filter(needsPayout);
        }

        // Payout (seller) failed/exhausted — needs admin attention
        if (payoutTroubleOnly) {
            rows = rows.filter(r => ['failed', 'exhausted'].includes(r.payoutStatus.toLowerCase()));
        }

        // Refund (buyer) failed/exhausted — needs admin attention
        if (refundTroubleOnly) {
            rows = rows.filter(r => ['failed', 'exhausted'].includes(r.buyerPayoutStatus.toLowerCase()));
        }

        // Provider filter
        if (providerFilter !== 'all') {
            rows = rows.filter(r => r.paymentProvider.toLowerCase() === providerFilter);
        }

        // Date range filter
        if (dateRange && dateRange[0] && dateRange[1]) {
            const from = dateRange[0].startOf('day').valueOf();
            const to   = dateRange[1].endOf('day').valueOf();
            rows = rows.filter(r => r.dateMs >= from && r.dateMs <= to);
        }

        // Sort by date
        rows = [...rows].sort((a, b) =>
            sortDir === 'desc' ? b.dateMs - a.dateMs : a.dateMs - b.dateMs
        );

        return rows;
    }, [allRows, searchText, orderStatusFilter, payoutStatusFilter, refundStatusFilter, needsPayoutOnly, payoutTroubleOnly, refundTroubleOnly, providerFilter, dateRange, sortDir]);

    // Summary counts for the filter chips
    const statusCounts = useMemo(() => {
        const map: Record<string, number> = {all: allRows.length};
        ORDER_STATUSES.forEach(s => {
            map[s] = allRows.filter(r => r.statusText.toLowerCase() === s).length;
        });
        return map;
    }, [allRows]);

    // Live count of orders that are paid & confirmed but still awaiting payout
    const needsPayoutCount = useMemo(() => allRows.filter(needsPayout).length, [allRows]);

    // Live counts of orders whose payout/refund attempt has failed or exhausted
    // its retries — these need an admin to look at them, surfaced as their own
    // quick-access chips (same idea as Needs Payout) rather than buried in the
    // Payout/Refund status dropdowns.
    const payoutTroubleCount = useMemo(
        () => allRows.filter(r => ['failed', 'exhausted'].includes(r.payoutStatus.toLowerCase())).length,
        [allRows]
    );
    const refundTroubleCount = useMemo(
        () => allRows.filter(r => ['failed', 'exhausted'].includes(r.buyerPayoutStatus.toLowerCase())).length,
        [allRows]
    );

    // Full column definitions keyed by ColConfig key
    const ALL_COLUMN_DEFS: Record<string, any> = {
        orderNo: {
            title: 'Order No', dataIndex: 'orderNo', key: 'orderNo',
            render: (v: string) => v
                ? <span style={{fontFamily:'monospace', fontSize:12}}>#{v}</span>
                : <span style={{color:'#ccc'}}>—</span>,
        },
        seller: {
            title: 'Seller', dataIndex: 'seller', key: 'seller',
            sorter: (a: DataType, b: DataType) => a.seller.localeCompare(b.seller),
        },
        buyer: {
            title: 'Buyer', dataIndex: 'buyer', key: 'buyer',
            sorter: (a: DataType, b: DataType) => a.buyer.localeCompare(b.buyer),
        },
        product: {
            title: 'Product', dataIndex: 'product', key: 'product',
        },
        rawAmount: {
            title: 'Buyer Paid (MWK)', dataIndex: 'rawAmount', key: 'rawAmount',
            render: (v: number) => numberWithCommas(v),
            sorter: (a: DataType, b: DataType) => a.rawAmount - b.rawAmount,
        },
        status: {
            title: 'Order Status', dataIndex: 'status', key: 'status',
        },
        paymentStatus: {
            title: 'Payment (Buyer)', dataIndex: 'paymentStatusText', key: 'paymentStatus',
            render: (t: string) => t
                ? <Tag severity={statusSeverity(t)} value={t} />
                : <span style={{color:'#ccc'}}>—</span>,
        },
        payoutStatus: {
            title: 'Payout (Seller)', dataIndex: 'payoutStatus', key: 'payoutStatus',
            render: (t: string, row: DataType) => {
                // A cancelled order was never confirmed/paid to the seller — don't let
                // ledgerPayoutState's "Pending clearance" fallback mislabel it.
                const ledger = row.statusText.toLowerCase() === 'cancelled' ? null : ledgerPayoutState(row);
                if (ledger) return <Tag severity={ledger.severity} value={ledger.label} />;
                return t
                    ? <Tag severity={statusSeverity(t)} value={t} />
                    : <span style={{color:'#ccc'}}>—</span>;
            },
        },
        buyerPayoutStatus: {
            title: 'Refund (Buyer)', dataIndex: 'buyerPayoutStatus', key: 'buyerPayoutStatus',
            sorter: (a: DataType, b: DataType) => a.buyerPayoutStatus.localeCompare(b.buyerPayoutStatus),
            render: (t: string) => t
                ? <Tag severity={statusSeverity(t)} value={t} />
                : <span style={{color:'#ccc'}}>—</span>,
        },
        paymentProvider: {
            title: 'Gateway', dataIndex: 'paymentProvider', key: 'paymentProvider',
            render: (t: string) => <span style={{textTransform:'capitalize'}}>{t || '—'}</span>,
        },
        paymentMethod: {
            title: 'Payment Method', dataIndex: 'paymentMethod', key: 'paymentMethod',
            render: (t: string) => t || '—',
        },
        date: {
            title: 'Date', dataIndex: 'dateMs', key: 'date',
            sortOrder: (sortDir === 'desc' ? 'descend' : 'ascend') as 'descend' | 'ascend',
            sorter: (a: DataType, b: DataType) => a.dateMs - b.dateMs,
            render: (_: any, row: DataType) => row.date || '—',
        },
        updatedAt: {
            title: 'Last Modified', dataIndex: 'updatedAtMs', key: 'updatedAt',
            sorter: (a: DataType, b: DataType) => a.updatedAtMs - b.updatedAtMs,
            render: (_: any, row: DataType) => row.updatedAt || '—',
        },
    };

    // Merge: if DEFAULT_COLS added new keys since the state was last initialised
    // (e.g. after a hot-reload that doesn't reset component state), append them
    // so they're always visible without requiring a hard page refresh.
    const mergedConfigs: ColConfig[] = (() => {
        const existing = new Set(colConfigs.map(c => c.key));
        const missing  = DEFAULT_COLS.filter(d => !existing.has(d.key));
        return missing.length ? [...colConfigs, ...missing] : colConfigs;
    })();

    // Build ordered, filtered column list
    const columns: ColumnsType<DataType> = [
        { title: '#', key: 'index', width: 50,
          render: (_: any, __: DataType, i: number) => i + 1 },
        ...mergedConfigs
            .filter(c => c.visible && ALL_COLUMN_DEFS[c.key])
            .map(c => ALL_COLUMN_DEFS[c.key]),
    ];

    const activeFilters = [
        orderStatusFilter !== 'all' && orderStatusFilter,
        needsPayoutOnly && 'needs payout',
        payoutTroubleOnly && 'payout failed/exhausted',
        refundTroubleOnly && 'refund failed/exhausted',
        payoutStatusFilter !== 'all' && `payout: ${payoutStatusFilter}`,
        refundStatusFilter !== 'all' && `refund: ${refundStatusFilter}`,
        providerFilter !== 'all' && providerFilter,
        dateRange && dateRange[0] && dateRange[1] && `${dateRange[0].format('DD MMM')} – ${dateRange[1].format('DD MMM YYYY')}`,
    ].filter(Boolean);

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">

                            {/* ── Header bar ── */}
                            <MDBRow>
                                <MDBCol>
                                    <Card className="my-2 p-2 font-JosefinSans overflow-hidden">
                                        <div className="d-flex pt-2 px-2 justify-content-between align-items-center flex-wrap" style={{gap:8}}>
                                            <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold", margin: 10}}>
                                                Transactions
                                                <span style={{fontSize:14, color:'#888', fontWeight:400, marginLeft:8}}>
                                                    ({displayRows.length}{displayRows.length !== allRows.length ? ` of ${allRows.length}` : ''})
                                                </span>
                                            </Text>
                                            <div style={{display:'flex', gap:8, alignItems:'center'}}>
                                                <Input
                                                    placeholder="Search by order #, seller, buyer, product, provider…"
                                                    value={searchText}
                                                    onChange={(e: any) => setSearchText(e.target.value)}
                                                    style={{minWidth: 300}}
                                                    contentRight={
                                                        searchText
                                                            ? <span style={{cursor:'pointer', color:'#aaa'}}
                                                                    onClick={() => setSearchText('')}>✕</span>
                                                            : <MDBIcon icon="search"/>
                                                    }
                                                />
                                                <ColumnManager cols={mergedConfigs} onChange={setColConfigs} />
                                            </div>
                                        </div>
                                    </Card>
                                </MDBCol>
                            </MDBRow>

                            {/* ── Filters ── */}
                            <MDBRow>
                                <MDBCol>
                                    <Card className="my-1 px-3 py-2 font-JosefinSans overflow-hidden">
                                        <div style={{display:'flex', flexWrap:'wrap', gap:16, alignItems:'center'}}>

                                            {/* Order status chips */}
                                            <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
                                                <span style={{fontSize:12, color:'#888', marginRight:2}}>Status:</span>
                                                {['all', ...ORDER_STATUSES].map(s => (
                                                    <button key={s}
                                                        onClick={() => setOrderStatusFilter(s)}
                                                        style={{
                                                            fontSize:12, padding:'3px 10px', borderRadius:20,
                                                            border: orderStatusFilter === s ? '1.5px solid #29327b' : '1px solid #ddd',
                                                            background: orderStatusFilter === s ? '#29327b' : '#fff',
                                                            color: orderStatusFilter === s ? '#fff' : '#444',
                                                            cursor:'pointer', whiteSpace:'nowrap',
                                                        }}>
                                                        {s === 'all' ? `All (${statusCounts.all})` : `${s.replace('_',' ')} (${statusCounts[s] ?? 0})`}
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Needs payout — paid & confirmed but payout empty or failed */}
                                            <button
                                                onClick={() => setNeedsPayoutOnly(v => !v)}
                                                title="Paid & confirmed orders whose seller payout is empty or failed"
                                                style={{
                                                    fontSize:12, padding:'3px 12px', borderRadius:20, fontWeight:600,
                                                    border: needsPayoutOnly ? '1.5px solid #b91c1c' : '1px solid #fca5a5',
                                                    background: needsPayoutOnly ? '#b91c1c' : (needsPayoutCount > 0 ? '#fef2f2' : '#fff'),
                                                    color: needsPayoutOnly ? '#fff' : (needsPayoutCount > 0 ? '#b91c1c' : '#bbb'),
                                                    cursor:'pointer', whiteSpace:'nowrap',
                                                }}>
                                                ⚠ Needs Payout ({needsPayoutCount})
                                            </button>

                                            {/* Payout (seller) failed/exhausted */}
                                            <button
                                                onClick={() => setPayoutTroubleOnly(v => !v)}
                                                title="Seller payout attempts that failed or exhausted their retries"
                                                style={{
                                                    fontSize:12, padding:'3px 12px', borderRadius:20, fontWeight:600,
                                                    border: payoutTroubleOnly ? '1.5px solid #b91c1c' : '1px solid #fca5a5',
                                                    background: payoutTroubleOnly ? '#b91c1c' : (payoutTroubleCount > 0 ? '#fef2f2' : '#fff'),
                                                    color: payoutTroubleOnly ? '#fff' : (payoutTroubleCount > 0 ? '#b91c1c' : '#bbb'),
                                                    cursor:'pointer', whiteSpace:'nowrap',
                                                }}>
                                                ⚠ Payout Failed ({payoutTroubleCount})
                                            </button>

                                            {/* Refund (buyer) failed/exhausted */}
                                            <button
                                                onClick={() => setRefundTroubleOnly(v => !v)}
                                                title="Buyer refund attempts that failed or exhausted their retries"
                                                style={{
                                                    fontSize:12, padding:'3px 12px', borderRadius:20, fontWeight:600,
                                                    border: refundTroubleOnly ? '1.5px solid #b91c1c' : '1px solid #fca5a5',
                                                    background: refundTroubleOnly ? '#b91c1c' : (refundTroubleCount > 0 ? '#fef2f2' : '#fff'),
                                                    color: refundTroubleOnly ? '#fff' : (refundTroubleCount > 0 ? '#b91c1c' : '#bbb'),
                                                    cursor:'pointer', whiteSpace:'nowrap',
                                                }}>
                                                ⚠ Refund Failed ({refundTroubleCount})
                                            </button>

                                            {/* Payout status */}
                                            <div style={{display:'flex', gap:4, alignItems:'center'}}>
                                                <span style={{fontSize:12, color:'#888', marginRight:2}}>Payout (seller):</span>
                                                <Select
                                                    size="small"
                                                    value={payoutStatusFilter}
                                                    onChange={setPayoutStatusFilter}
                                                    style={{width:140}}>
                                                    <Option value="all">All</Option>
                                                    {PAYOUT_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
                                                </Select>
                                            </div>

                                            {/* Refund (buyer) status */}
                                            <div style={{display:'flex', gap:4, alignItems:'center'}}>
                                                <span style={{fontSize:12, color:'#888', marginRight:2}}>Refund (buyer):</span>
                                                <Select
                                                    size="small"
                                                    value={refundStatusFilter}
                                                    onChange={setRefundStatusFilter}
                                                    style={{width:140}}>
                                                    <Option value="all">All</Option>
                                                    {PAYOUT_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
                                                </Select>
                                            </div>

                                            {/* Provider */}
                                            <div style={{display:'flex', gap:4, alignItems:'center'}}>
                                                <span style={{fontSize:12, color:'#888', marginRight:2}}>Provider:</span>
                                                <Select
                                                    size="small"
                                                    value={providerFilter}
                                                    onChange={setProviderFilter}
                                                    style={{width:130}}>
                                                    <Option value="all">All</Option>
                                                    {PROVIDERS.map(p => <Option key={p} value={p}>{p}</Option>)}
                                                </Select>
                                            </div>

                                            {/* Date range */}
                                            <div style={{display:'flex', gap:4, alignItems:'center'}}>
                                                <span style={{fontSize:12, color:'#888', marginRight:2}}>Date:</span>
                                                <RangePicker
                                                    size="small"
                                                    format="DD MMM YYYY"
                                                    onChange={(vals) => setDateRange(vals as [Dayjs|null, Dayjs|null] | null)}
                                                    style={{fontSize:12}}
                                                />
                                            </div>

                                            {/* Sort direction */}
                                            <div style={{display:'flex', gap:4, alignItems:'center', marginLeft:'auto'}}>
                                                <span style={{fontSize:12, color:'#888'}}>Sort:</span>
                                                <button
                                                    onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                                    style={{
                                                        fontSize:12, padding:'3px 10px', borderRadius:6,
                                                        border:'1px solid #ddd', background:'#fff',
                                                        cursor:'pointer', display:'flex', alignItems:'center', gap:4,
                                                    }}>
                                                    <MDBIcon icon={sortDir === 'desc' ? 'sort-amount-down' : 'sort-amount-up'} />
                                                    {sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Active filter pills */}
                                        {activeFilters.length > 0 && (
                                            <div style={{display:'flex', gap:6, marginTop:10, flexWrap:'wrap', alignItems:'center'}}>
                                                <span style={{fontSize:11, color:'#aaa'}}>Active:</span>
                                                {activeFilters.map((f, i) => (
                                                    <span key={i} style={{
                                                        fontSize:11, background:'#eef2ff', color:'#29327b',
                                                        borderRadius:12, padding:'2px 10px', border:'1px solid #c7d2fe',
                                                    }}>{f as string}</span>
                                                ))}
                                                <button
                                                    onClick={() => {
                                                        setOrderStatusFilter('all');
                                                        setPayoutStatusFilter('all');
                                                        setRefundStatusFilter('all');
                                                        setNeedsPayoutOnly(false);
                                                        setPayoutTroubleOnly(false);
                                                        setRefundTroubleOnly(false);
                                                        setProviderFilter('all');
                                                        setDateRange(null);
                                                        setSearchText('');
                                                    }}
                                                    style={{fontSize:11, color:'#ef4444', background:'none',
                                                            border:'none', cursor:'pointer', padding:'0 4px'}}>
                                                    Clear all
                                                </button>
                                            </div>
                                        )}
                                    </Card>
                                </MDBCol>
                            </MDBRow>

                            {/* ── Table ── */}
                            <MDBRow>
                                <MDBCol>
                                    <Card className="my-2 font-JosefinSans overflow-hidden">
                                        <Table
                                            columns={columns}
                                            dataSource={displayRows}
                                            loading={loading}
                                            rowKey="key"
                                            size="middle"
                                            scroll={{x: 'max-content'}}
                                            onChange={(_pagination: any, _filters: any, sorter: any) => {
                                                // Sync column-header sort click with the sortDir button
                                                if (!Array.isArray(sorter) && sorter.columnKey === 'date' && sorter.order) {
                                                    setSortDir(sorter.order === 'ascend' ? 'asc' : 'desc');
                                                }
                                            }}
                                            onRow={(record) => ({
                                                onClick: () => {
                                                    setSelectedOrder(record.raw);
                                                    setDrawerOpen(true);
                                                },
                                                style: {
                                                    cursor: 'pointer',
                                                    ...(needsPayout(record)
                                                        ? {background: '#fef2f2', boxShadow: 'inset 3px 0 0 #b91c1c'}
                                                        : {}),
                                                },
                                            })}
                                            pagination={{
                                                defaultPageSize: 25,
                                                showSizeChanger: true,
                                                pageSizeOptions: ['25', '50', '100'],
                                                showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                                            }}
                                        />
                                    </Card>
                                </MDBCol>
                            </MDBRow>

                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <TransactionDrawer
                order={selectedOrder}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            />
        </>
    );
};

export default Transactions;
